import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Icon } from "@/components/Icon";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ui/confirm";
import LoadError from "@/components/ui/LoadError";
import EmptyState from "@/components/ui/EmptyState";
import { formatOdds, useOddsFormat } from "@/lib/odds";

const fmtAmt = (amt, currency) => currency === "cash" ? `$${(Number(amt) || 0).toFixed(2)}` : `${(Number(amt) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PCoins`;

// Number only (no currency word/symbol) — the icon carries the currency, so the
// spelled "PCoins" word is redundant and blows out narrow rows on mobile.
const amtNum = (amt, currency) => currency === "cash"
  ? `$${(Number(amt) || 0).toFixed(2)}`
  : (Number(amt) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Amount with the matching currency icon (PCash icon for cash, PCoins icon for coins).
function Amt({ value, currency, size = 14, className = "" }) {
  const cash = currency === "cash";
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 tabular-nums ${className}`}>
      <Icon name={cash ? "pnrcash" : "pnrcoin"} size={size} className={`shrink-0 ${cash ? "text-pnr-cash" : "text-pnr-coin"}`} />{amtNum(value, currency)}
    </span>
  );
}
const isSettled = (s) => ["won", "lost", "push", "refunded", "cashed_out"].includes(s);

const PILL = {
  pending: { label: "OPEN", bg: "#1F1F23", color: "#a1a1aa" },
  won: { label: "WON", bg: "rgba(0,214,50,.12)", color: "#00D632", ring: "rgba(0,214,50,.35)" },
  lost: { label: "LOST", bg: "rgba(239,68,68,.12)", color: "#EF4444", ring: "rgba(239,68,68,.32)" },
  push: { label: "PUSH · REFUNDED", bg: "#1F1F23", color: "#a1a1aa" },
  refunded: { label: "REFUNDED", bg: "#1F1F23", color: "#a1a1aa" },
  cashed_out: { label: "CASHED OUT", bg: "#1F1F23", color: "#a1a1aa" },
};

function StatusPill({ status, live }) {
  const s = PILL[status] || PILL.pending;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider" style={{ background: s.bg, color: s.color, boxShadow: s.ring ? `inset 0 0 0 1px ${s.ring}` : undefined }}>
      {live && status === "pending" && <span className="h-1.5 w-1.5 rounded-full bg-[#EF4444]" style={{ animation: "pnrPulse 1.6s ease-out infinite" }} />}
      {s.label}
    </span>
  );
}

function LegDot({ status }) {
  if (status === "won") return <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#00D632]"><Icon name="check" size={11} className="text-[#0A0A0A]" /></span>;
  if (status === "lost") return <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ boxShadow: "inset 0 0 0 2px #EF4444" }}><Icon name="x" size={11} className="text-[#EF4444]" /></span>;
  const ring = status === "live" ? "#EF4444" : status === "push" ? "#52525b" : "#3F3F46";
  return <span className="h-5 w-5 shrink-0 rounded-full" style={{ boxShadow: `inset 0 0 0 2px ${ring}` }} />;
}

// Loading skeleton matching the bet-card layout (§4.10) — not a bare spinner.
function MyBetsSkeleton() {
  return (
    <div className="space-y-3" data-testid="mybets-skeleton" aria-hidden="true">
      {[0, 1].map((i) => (
        <div key={i} className="overflow-hidden rounded-[18px]" style={{ background: "linear-gradient(145deg, #141419 0%, #141417 45%, #0D0D11 100%)", boxShadow: "inset 0 0 0 1px #1F1F23, inset 0 1px 0 rgba(255,255,255,.07)" }}>
          <div className="flex items-center justify-between px-4 pt-3.5 sm:px-5">
            <span className="h-3.5 w-28 animate-pulse rounded bg-surface-4" />
            <span className="h-5 w-16 animate-pulse rounded-md bg-surface-4" />
          </div>
          <div className="mt-3 space-y-3 px-4 pb-4 sm:px-5">
            <div className="flex items-center gap-3"><span className="h-5 w-5 animate-pulse rounded-full bg-surface-4" /><span className="h-4 flex-1 animate-pulse rounded bg-surface-4" /></div>
            <div className="h-11 animate-pulse rounded-xl bg-surface-4" />
          </div>
        </div>
      ))}
    </div>
  );
}

function BetCard({ card, oddsFmt, cashing }) {
  const { kind, legs, status, currency, stake, createdAt, id, price, cashQuote, onCash } = card;
  // Real in-play dot: only pulse when the underlying event is actually live now (backend in_play),
  // not for every pending bet (UIB-08).
  const live = !!card.inPlay && status === "pending";
  const right = status === "won" ? { label: "PAID", amt: card.payout, green: true }
    : status === "lost" ? { label: "RETURNED", amt: 0 }
    : (status === "push" || status === "refunded") ? { label: "REFUNDED", amt: stake }
    : status === "cashed_out" ? { label: "CASHED OUT", amt: card.cashoutValue }
    : { label: "TO RETURN", amt: card.potentialPayout };
  const canCash = status === "pending" && cashQuote && cashQuote.available;
  return (
    <div className="overflow-hidden rounded-[18px]" style={{ background: "linear-gradient(145deg, #141419 0%, #141417 45%, #0D0D11 100%)", boxShadow: "inset 0 0 0 1px #1F1F23, inset 0 1px 0 rgba(255,255,255,.07), inset 0 -1px 0 rgba(0,0,0,.55), 0 10px 26px rgba(0,0,0,.22)" }} data-testid={kind === "parlay" ? `parlay-${id}` : `bet-${id}`}>
      {/* header */}
      <div className="flex items-center justify-between gap-2 px-4 pt-3.5 sm:px-5">
        <span className="flex items-center gap-2">
          <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#71717a]">{kind === "parlay" ? `${legs.length}-leg parlay` : "Straight"}</span>
          <span className="font-display text-[14px] font-extrabold tabular-nums text-white">{formatOdds(price, oddsFmt)}</span>
        </span>
        <StatusPill status={status} live={live} data-testid={kind === "parlay" ? `parlay-status-${id}` : `bet-status-${id}`} />
      </div>
      {/* legs */}
      <div className="mt-2.5 px-4 sm:px-5">
        {legs.map((l, i) => (
          <div key={i} className="flex items-center gap-3" style={{ minHeight: 56, borderTop: i > 0 ? "1px solid #1A1A1E" : "none" }}>
            <LegDot status={l.status} />
            <span className="min-w-0 flex-1 py-2">
              <span className="block truncate text-[15px] font-bold text-white">{l.outcome_label}</span>
              <span className="block truncate text-[11px] text-[#71717a]">{l.event_label}</span>
            </span>
            <span className="shrink-0 font-display text-sm font-extrabold tabular-nums text-[#a1a1aa]">{formatOdds(l.odds, oddsFmt)}</span>
          </div>
        ))}
      </div>
      {/* cash out */}
      {canCash && (
        <div className="px-4 pt-3 sm:px-5">
          <button data-testid={kind === "parlay" ? `parlay-cashout-btn-${id}` : `cashout-btn-${id}`} disabled={cashing[id]} onClick={onCash}
            className="flex h-11 w-full items-center justify-center gap-1 rounded-xl px-4 text-[13px] font-bold text-[#0A0A0A] transition-colors focus-ring disabled:opacity-60" style={{ background: "#FAFAFA" }}>
            {cashing[id] ? "Cashing out…" : <>Cash out <Amt value={cashQuote.value} currency={currency} size={13} /></>}
          </button>
        </div>
      )}
      {/* footer */}
      <div className="mt-3 flex items-center justify-between gap-2 px-4 py-3 sm:px-5" style={{ background: "#0F0F12", borderTop: "1px solid #1A1A1E" }}>
        <div className="min-w-0">
          <p className="flex items-center gap-1 truncate text-xs text-[#a1a1aa]">Stake <Amt value={stake} currency={currency} size={12} className="font-semibold text-white" /></p>
          <p className="truncate text-[11px] text-[#71717a]">{createdAt} · {id.slice(-6).toUpperCase()}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#71717a]">{right.label}</p>
          <p className="flex items-center justify-end font-display text-[20px] font-extrabold tabular-nums" style={{ color: right.green ? "#00D632" : "#fff" }}><Amt value={right.amt} currency={currency} size={16} /></p>
        </div>
      </div>
    </div>
  );
}

export default function MyBets() {
  const { refresh } = useAuth();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const oddsFmt = useOddsFormat();
  const [bets, setBets] = useState(null);
  const [parlays, setParlays] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [pQuotes, setPQuotes] = useState({});
  const [cashing, setCashing] = useState({});
  const [loadErr, setLoadErr] = useState(false);
  const [tab, setTab] = useState("open");       // open | settled
  const [chip, setChip] = useState("all");       // all | won | lost | push | cashed_out

  const loadBets = useCallback(async () => {
    try {
      const [b, p] = await Promise.all([api.get("/bets/me"), api.get("/parlays/me")]);
      setBets(b.data);
      setParlays(p.data);
      setLoadErr(false);
      return { bets: b.data, parlays: p.data };
    } catch (e) {
      // A real load failure must NOT render as "no settled bets" — flag it so the UI can
      // offer a retry distinct from a genuinely-empty history (UIB-08).
      setLoadErr(true);
      return { bets: bets || [], parlays: parlays || [] };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadQuotes = useCallback(async (data) => {
    const list = data?.bets || [];
    const plist = data?.parlays || [];
    const pending = list.filter((b) => b.status === "pending");
    const entries = await Promise.all(pending.map(async (b) => {
      try { const { data } = await api.get(`/bets/${b.id}/cashout`); return [b.id, data]; }
      catch { return [b.id, { available: false }]; }
    }));
    setQuotes(Object.fromEntries(entries));
    const pPending = plist.filter((p) => p.status === "pending");
    const pEntries = await Promise.all(pPending.map(async (p) => {
      try { const { data } = await api.get(`/parlays/${p.id}/cashout`); return [p.id, data]; }
      catch { return [p.id, { available: false }]; }
    }));
    setPQuotes(Object.fromEntries(pEntries));
  }, []);

  useEffect(() => {
    loadBets().then(loadQuotes);
    const t = setInterval(async () => { const data = await loadBets(); await loadQuotes(data); }, 5000);
    return () => clearInterval(t);
  }, [loadBets, loadQuotes]);

  const cashOut = async (b) => {
    const q = quotes[b.id];
    const ok = await confirm({
      title: "Cash out this bet?",
      body: q && q.value != null
        ? `You'll lock in ${fmtAmt(q.value, b.currency)} now and forfeit any further winnings on this bet. The offer may change before it's accepted.`
        : "You'll settle this bet now and forfeit any further winnings. The offer may change before it's accepted.",
      confirmText: "Cash out",
    });
    if (!ok) return;
    setCashing((p) => ({ ...p, [b.id]: true }));
    try {
      const { data } = await api.post(`/bets/${b.id}/cashout`);
      toast.success(`Cashed out for ${fmtAmt(data.value, b.currency)}`);
      const d = await loadBets();
      await loadQuotes(d);
      await refresh();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Cash out failed");
    } finally {
      setCashing((p) => ({ ...p, [b.id]: false }));
    }
  };

  const cashOutParlay = async (p) => {
    const q = pQuotes[p.id];
    const ok = await confirm({
      title: "Cash out this parlay?",
      body: q && q.value != null
        ? `You'll lock in ${fmtAmt(q.value, p.currency)} now and forfeit any further winnings on this parlay. The offer may change before it's accepted.`
        : "You'll settle this parlay now and forfeit any further winnings. The offer may change before it's accepted.",
      confirmText: "Cash out",
    });
    if (!ok) return;
    setCashing((s) => ({ ...s, [p.id]: true }));
    try {
      const { data } = await api.post(`/parlays/${p.id}/cashout`);
      toast.success(`Parlay cashed out for ${fmtAmt(data.value, p.currency)}`);
      const d = await loadBets();
      await loadQuotes(d);
      await refresh();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Cash out failed");
    } finally {
      setCashing((s) => ({ ...s, [p.id]: false }));
    }
  };

  // Unify bets + parlays into a single card model.
  const cards = useMemo(() => {
    const out = [];
    (parlays || []).forEach((p) => out.push({
      id: p.id, kind: "parlay", legs: p.legs, status: p.status, currency: p.currency, stake: p.stake,
      createdAt: new Date(p.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
      price: p.combined_odds, potentialPayout: p.potential_payout, payout: p.payout, cashoutValue: p.cashout_value || p.payout,
      inPlay: !!p.in_play,
      cashQuote: pQuotes[p.id], onCash: () => cashOutParlay(p), _t: p.created_at,
    }));
    (bets || []).forEach((b) => out.push({
      id: b.id, kind: "straight", legs: [{ event_label: b.event_label, outcome_label: b.outcome_label, odds: b.odds, status: b.status === "pending" ? "pending" : b.status }],
      status: b.status, currency: b.currency, stake: b.stake,
      createdAt: new Date(b.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
      price: b.odds, potentialPayout: b.potential_payout, payout: b.potential_payout, cashoutValue: b.cashout_value,
      inPlay: !!b.in_play,
      cashQuote: quotes[b.id], onCash: () => cashOut(b), _t: b.created_at,
    }));
    out.sort((a, b) => new Date(b._t) - new Date(a._t));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bets, parlays, quotes, pQuotes]);

  const openCards = cards.filter((c) => c.status === "pending");
  const settledCards = cards.filter((c) => isSettled(c.status));
  const chipMatch = (c) => chip === "all" || (chip === "push" ? (c.status === "push" || c.status === "refunded") : c.status === chip);
  const shown = tab === "open" ? openCards : settledCards.filter(chipMatch);

  if (!bets) return (
    <div className="min-h-screen bg-surface-1 text-white">
      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <p className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.24em] text-pnr-green">Your bets</p>
        <h1 className="font-display text-[26px] font-extrabold leading-none tracking-[-.04em] sm:text-[40px]">My Bets</h1>
        <div className="mt-6 max-w-2xl">
          {loadErr
            ? <LoadError onRetry={() => loadBets().then(loadQuotes)} message="We couldn't load your bets." testid="mybets-load-error" />
            : <MyBetsSkeleton />}
        </div>
      </div>
    </div>
  );

  const Tab = ({ id, label, count }) => (
    <button type="button" onClick={() => setTab(id)} data-testid={`mybets-tab-${id}`} aria-selected={tab === id}
      className="flex h-[46px] items-center gap-1.5 whitespace-nowrap rounded-t-md focus-ring sm:h-[50px]"
      style={tab === id ? { font: "700 15px Outfit", color: "#fff", boxShadow: "inset 0 -2px 0 #00D632" } : { font: "600 15px Outfit", color: "#8A8A93" }}>
      {label}<span className="tabular-nums" style={{ font: "700 12px Manrope", color: tab === id ? "#71717a" : "#52525b" }}>{count}</span>
    </button>
  );
  const Chip = ({ id, label, count }) => (
    <button type="button" onClick={() => setChip(id)} aria-pressed={chip === id} className="flex h-11 shrink-0 items-center gap-1.5 rounded-[11px] px-3.5 text-[13px] transition-colors focus-ring sm:h-8"
      style={chip === id ? { background: "#FAFAFA", color: "#0A0A0A", fontWeight: 700 } : { background: "#141417", boxShadow: "inset 0 0 0 1px #26262B", color: "#a1a1aa", fontWeight: 600 }}>
      {label}{count != null && <span className="tabular-nums" style={{ font: "700 11px Manrope", color: chip === id ? "#52525b" : "#71717a" }}>{count}</span>}
    </button>
  );
  const settledCount = (s) => s === "push" ? settledCards.filter((c) => c.status === "push" || c.status === "refunded").length : settledCards.filter((c) => c.status === s).length;

  return (
    <div className="min-h-screen bg-surface-1 text-white">
      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <p className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.24em] text-pnr-green">Your bets</p>
        <h1 className="font-display text-[26px] font-extrabold leading-none tracking-[-.04em] sm:text-[40px]">My Bets</h1>

        {/* tabs */}
        <div className="mt-5 flex gap-7 border-b border-surface-border" role="tablist" aria-label="Bet status">
          <Tab id="open" label="Open" count={openCards.length} />
          <Tab id="settled" label="Settled" count={settledCards.length} />
        </div>

        {/* settled chips */}
        {tab === "settled" && (
          <div className="no-scrollbar -mr-4 mt-4 flex gap-2 overflow-x-auto pb-1 sm:mr-0">
            <Chip id="all" label="All" count={settledCards.length} />
            <Chip id="won" label="Won" count={settledCount("won")} />
            <Chip id="lost" label="Lost" count={settledCount("lost")} />
            <Chip id="push" label="Push" count={settledCount("push")} />
            <Chip id="cashed_out" label="Cashed out" count={settledCount("cashed_out")} />
          </div>
        )}

        {/* A background refresh failed while we already have data — tell the player the list may
            be out of date, distinct from a genuinely-empty history (UIB-08). */}
        {loadErr && (
          <div className="mt-4"><LoadError onRetry={() => loadBets().then(loadQuotes)} message="We couldn't refresh your bets — this list may be out of date." testid="mybets-refresh-error" /></div>
        )}

        {/* cards */}
        <div className="mt-5 space-y-3">
          {shown.length === 0 ? (
            <EmptyState
              icon="mybets"
              testid="mybets-empty"
              message={tab === "open"
                ? "No open bets. Picks you place will show here while they're live."
                : chip === "all" ? "No settled bets yet." : `No ${chip === "cashed_out" ? "cashed out" : chip} bets yet.`}
              action={tab === "open" ? { label: "Browse the board", icon: "sportsbook", onClick: () => navigate("/sportsbook") } : null}
            />
          ) : (
            shown.map((c) => <BetCard key={`${c.kind}-${c.id}`} card={c} oddsFmt={oddsFmt} cashing={cashing} />)
          )}
        </div>
      </div>
    </div>
  );
}
